import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { QontyEmptyState } from '@components/QontyEmptyState';
import { QontyLoading } from '@components/QontyLoading';
import { SingleTabScreen } from '@components/General/SingleTabScreen';
import { useTimezone } from '@contexts/useTimezone';
import { Application } from '@core/Application';
import { Organization } from '@models/Organization';
import { AppAdoptionService } from '@services/AppAdoptionService';
import { CheckButton, GhostButton, HollowedButton, IconButton, NormalButton, RefreshButton } from '@shared/designSystem/Button';
import {
    CardContainer,
    HorizontalContainer,
    TableCell,
    TableHeaderCell,
    TableHeaderContainer,
    TableRowContainer,
    VerticalContainer,
} from '@shared/designSystem/Containers';
import DetailField from '@shared/designSystem/DetailField';
import FilterPillGroup, { FilterPillOption } from '@shared/designSystem/FilterPillGroup';
import { Modal } from '@shared/designSystem/messages/Modal';
import { ToastManager } from '@shared/designSystem/messages/ToastManager';
import SearchBar from '@shared/designSystem/SearchBar';
import { StatusPill } from '@shared/designSystem/StatusPill';
import { SystemIcon } from '@shared/designSystem/SystemIcon';
import { SmallText, Text } from '@shared/designSystem/Texts';
import UserCard from '@shared/designSystem/UserCard';
import designSystemStyles from '@shared/styles/designSystemStyles';
import {
    AdoptionPlatformFilter,
    AdoptionStatusFilter,
    adoptionStatusDotColors,
    AppAdoptionTableRow,
    useAppAdoptionScreenViewModel,
} from './AppAdoptionScreenViewModel';
import SendInvitationsModalScreen from './SendInvitationsModalScreen';

const columnWidths = {
    check: "4%",
    user: "26%",
    empresa: "15%",
    status: "19%",
    platform: "12%",
    lastActivity: "13%",
    actions: "11%",
};

interface AppAdoptionScreenProps {
    org?: Organization;
}

const AppAdoptionScreen: React.FC<AppAdoptionScreenProps> = ({ org }) => {
    const { timezone } = useTimezone();
    const appAdoptionService = useMemo(() => {
        return new AppAdoptionService(Application.shared().coreDependencies.httpClient);
    }, []);
    const viewModel = useAppAdoptionScreenViewModel({
        organizationID: org?.id,
        timezone,
        appAdoptionService,
    });
    const [showListInFirstUse, setShowListInFirstUse] = useState(false);

    useEffect(() => {
        setShowListInFirstUse(false);
    }, [org?.id]);

    if (org && !org.newFlags?.AppAdoptionWeb) {
        return <Navigate to="/" replace />;
    }

    const statusFilterOptions: FilterPillOption[] = [
        { id: 'all', label: 'Todos'.localized(), count: viewModel.counts.total },
        {
            id: 'withApp',
            label: 'Con la app'.localized(),
            count: viewModel.counts.withApp,
            dotColor: designSystemStyles.colorStatusSuccess,
        },
        {
            id: 'withoutApp',
            label: 'Sin la app'.localized(),
            count: viewModel.counts.withoutApp,
            dotColor: designSystemStyles.colorStatusNeutral,
        },
        {
            id: 'invited',
            label: 'Invitados'.localized(),
            count: viewModel.counts.invited,
            dotColor: designSystemStyles.colorStatusActive,
        },
    ];

    const platformFilterOptions: FilterPillOption[] = [
        { id: 'all', label: 'Todas'.localized() },
        { id: 'android', label: 'Android' },
        { id: 'ios', label: 'iOS' },
    ];

    const handleConfirmInvitations = async () => {
        const result = await viewModel.sendInvitations();
        if (result.kind === 'success') {
            ToastManager.shared().showSuccessMessage(result.message);
            return;
        }
        ToastManager.shared().showErrorMessage(result.message);
    };

    const renderStatusCell = (row: AppAdoptionTableRow) => (
        <VerticalContainer gap={designSystemStyles.gapXxxSm} alignment="left" width="100%">
            <StatusPill label={row.statusLabel} dotColor={adoptionStatusDotColors[row.status]} />
            {row.expiryLabel ? (
                <SmallText text={row.expiryLabel} color={designSystemStyles.colorTextMuted} />
            ) : null}
        </VerticalContainer>
    );

    const renderPlatformCell = (row: AppAdoptionTableRow) => {
        if (!row.platform) {
            return <Text text={"—"} color={designSystemStyles.colorTextMuted} />;
        }
        return (
            <VerticalContainer gap={designSystemStyles.gapXxxSm} alignment="left" width="100%">
                <HorizontalContainer gap={designSystemStyles.gapXxxSm} alignment="center" width="100%">
                    <SystemIcon
                        type={row.platform === 'ios' ? 'apple' : 'android'}
                        color={designSystemStyles.colorTextSecondary}
                        size="small"
                    />
                    <Text text={row.platformLabel} />
                </HorizontalContainer>
                {row.appVersionLabel ? (
                    <SmallText text={row.appVersionLabel} color={designSystemStyles.colorTextMuted} />
                ) : null}
            </VerticalContainer>
        );
    };

    const renderActionsCell = (row: AppAdoptionTableRow) => {
        switch (row.status) {
            case 'installed':
                return (
                    <>
                        <IconButton
                            iconType="eye"
                            onClick={() => viewModel.openDeviceDetails(row)}
                            tooltip={'Ver dispositivo'.localized()}
                        />
                        <IconButton
                            iconType="reset"
                            onClick={() => viewModel.requestReinstallInvite(row)}
                            disabled={viewModel.isSendingInvitations}
                            tooltip={'Reenviar invitación (reinstalación)'.localized()}
                        />
                    </>
                );
            case 'invited':
                return (
                    <IconButton
                        iconType="bell"
                        onClick={() => viewModel.requestSingleInvite(row)}
                        disabled={viewModel.isSendingInvitations}
                        tooltip={'Reenviar invitación'.localized()}
                    />
                );
            case 'noWhatsApp':
                return (
                    <IconButton
                        iconType="send"
                        onClick={() => undefined}
                        disabled={true}
                        tooltip={'No se puede invitar sin WhatsApp'.localized()}
                    />
                );
            default:
                return (
                    <IconButton
                        iconType="send"
                        onClick={() => viewModel.requestSingleInvite(row)}
                        disabled={viewModel.isSendingInvitations}
                        tooltip={'Invitar por WhatsApp'.localized()}
                    />
                );
        }
    };

    // First use: nobody has the app and nobody was invited yet. The table would be a
    // wall of "Sin instalar", so the screen leads with the one useful action instead.
    const isFirstUse = !viewModel.loading
        && !viewModel.loadErrorMessage
        && viewModel.counts.total > 0
        && viewModel.counts.withApp === 0
        && viewModel.counts.invited === 0;

    const tableView = (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: designSystemStyles.gapMd, width: "100%" }}>
            <TableHeaderContainer height={designSystemStyles.sizeRowMedium}>
                <HorizontalContainer width={columnWidths.check}>
                    <CheckButton
                        text=""
                        selected={viewModel.allFilteredSelected}
                        onClick={viewModel.toggleSelectAll}
                        disabled={!viewModel.hasInvitableFilteredRows}
                    />
                </HorizontalContainer>
                <TableHeaderCell text={'Colaborador'.localized()} width={columnWidths.user} />
                <TableHeaderCell text={'Empresa'.localized()} width={columnWidths.empresa} />
                <TableHeaderCell text={'Estado'.localized()} width={columnWidths.status} />
                <TableHeaderCell text={'Plataforma'.localized()} width={columnWidths.platform} />
                <TableHeaderCell text={'Última actualización'.localized()} width={columnWidths.lastActivity} />
                <TableHeaderCell text={'Acciones'.localized()} width={columnWidths.actions} justifyContent="flex-end" />
            </TableHeaderContainer>
            <div style={{ display: "flex", flexDirection: "column", gap: designSystemStyles.gapNone, overflowY: "auto", flex: 1, minHeight: 0, width: "100%" }}>
                {viewModel.rows.length === 0 ? (
                    <VerticalContainer width="100%" height="100%" alignment="center" justifyContent="center">
                        <QontyEmptyState
                            text={'Ningún colaborador coincide con el filtro'.localized()}
                            darkMode={false}
                            fullSize={false}
                        />
                    </VerticalContainer>
                ) : viewModel.rows.map((row) => (
                    <TableRowContainer key={row.userID}>
                        <HorizontalContainer width={columnWidths.check}>
                            {row.canInvite ? (
                                <CheckButton
                                    text=""
                                    selected={viewModel.selectedUserIDs.includes(row.userID)}
                                    onClick={() => viewModel.toggleUser(row.userID)}
                                />
                            ) : null}
                        </HorizontalContainer>
                        <HorizontalContainer width={columnWidths.user} minWidth="0">
                            <UserCard
                                name={row.name}
                                subtitle={row.phoneLabel || 'Sin número'.localized()}
                                imageURL={row.imageURL}
                                width="100%"
                            />
                        </HorizontalContainer>
                        <TableCell
                            text={row.empresa || 'N/D'.localized()}
                            width={columnWidths.empresa}
                            truncate={true}
                        />
                        <HorizontalContainer width={columnWidths.status} minWidth="0">
                            {renderStatusCell(row)}
                        </HorizontalContainer>
                        <HorizontalContainer width={columnWidths.platform} minWidth="0">
                            {renderPlatformCell(row)}
                        </HorizontalContainer>
                        <TableCell
                            text={row.lastActivityLabel}
                            width={columnWidths.lastActivity}
                            truncate={true}
                            small={true}
                        />
                        <HorizontalContainer width={columnWidths.actions} justifyContent="flex-end">
                            {renderActionsCell(row)}
                        </HorizontalContainer>
                    </TableRowContainer>
                ))}
            </div>
            {viewModel.selectedCount > 0 ? (
                <CardContainer width="100%" boxShadow={designSystemStyles.shadowCard}>
                    <HorizontalContainer
                        width="100%"
                        alignment="center"
                        justifyContent="space-between"
                        gap={designSystemStyles.gapSm}
                    >
                        <Text
                            text={viewModel.selectedCount === 1
                                ? '1 colaborador seleccionado'.localized()
                                : `${viewModel.selectedCount} ${'colaboradores seleccionados'.localized()}`}
                        />
                        <HorizontalContainer justifyContent="flex-end" gap={designSystemStyles.gapSm} width="auto">
                            <GhostButton
                                text={'Cancelar'.localized()}
                                onClick={viewModel.clearSelection}
                                disabled={viewModel.isSendingInvitations}
                            />
                            <NormalButton
                                text={'Enviar invitación por WhatsApp'.localized()}
                                onClick={viewModel.requestBulkInvite}
                                disabled={viewModel.isSendingInvitations}
                                width="290px"
                            />
                        </HorizontalContainer>
                    </HorizontalContainer>
                </CardContainer>
            ) : null}
        </div>
    );

    const firstUseView = (
        <VerticalContainer
            width="100%"
            height="100%"
            alignment="center"
            justifyContent="center"
            backgroundColor={designSystemStyles.colorBackgroundGray3}
            borderRadius={designSystemStyles.radiusItem}
            gap={designSystemStyles.gapMd}
        >
            <QontyEmptyState
                text={'Ningún colaborador tiene la app instalada todavía.'.localized()}
                darkMode={false}
                fullSize={false}
            />
            <NormalButton
                text={'Invitar a todos por WhatsApp'.localized()}
                onClick={viewModel.requestInviteAll}
                width="300px"
            />
            <HollowedButton
                text={'Elegir colaboradores'.localized()}
                onClick={() => setShowListInFirstUse(true)}
                width="300px"
            />
        </VerticalContainer>
    );

    const content = (
        <VerticalContainer
            gap={designSystemStyles.gapMd}
            alignment="left"
            height="100%"
            width="100%"
        >
            <HorizontalContainer height={designSystemStyles.sizeRowMedium} width="100%" gap={designSystemStyles.gapMd}>
                <SearchBar
                    placeholder={'Buscar colaborador…'.localized()}
                    value={viewModel.searchField}
                    onChange={viewModel.setSearchField}
                    disabled={viewModel.counts.total === 0}
                />
                <RefreshButton text="" onClick={() => void viewModel.refresh()} />
                <HorizontalContainer justifyContent="right">
                    <SmallText
                        text={`${viewModel.rows.length} ${'de'.localized()} ${viewModel.counts.total} ${'colaboradores'.localized()}`}
                    />
                </HorizontalContainer>
            </HorizontalContainer>
            <HorizontalContainer width="100%" alignment="center" gap={designSystemStyles.gapSm}>
                <FilterPillGroup
                    options={statusFilterOptions}
                    selectedId={viewModel.statusFilter}
                    onSelect={(id) => viewModel.setStatusFilter(id as AdoptionStatusFilter)}
                    ariaLabel={'Filtrar por estado de adopción'.localized()}
                    size="small"
                    wrap={false}
                    isLoading={viewModel.loading}
                />
                <HorizontalContainer justifyContent="right" width="auto">
                    <FilterPillGroup
                        options={platformFilterOptions}
                        selectedId={viewModel.platformFilter}
                        onSelect={(id) => viewModel.setPlatformFilter(id as AdoptionPlatformFilter)}
                        ariaLabel={'Filtrar por plataforma'.localized()}
                        size="small"
                        wrap={false}
                        isLoading={viewModel.loading}
                    />
                </HorizontalContainer>
            </HorizontalContainer>

            {/* Bounded flex slot: the toolbar rows above take their natural height and
                the table (or state view) fills exactly what remains, scrolling inside
                instead of pushing past the card bottom. */}
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, width: "100%" }}>
            {viewModel.loading ? (
                <QontyLoading
                    text={'Cargando adopción de la app...'.localized()}
                    withQonty={true}
                    darkMode={false}
                />
            ) : viewModel.loadErrorMessage && viewModel.allRows.length === 0 ? (
                <VerticalContainer width="100%" height="100%" alignment="center" justifyContent="center" gap={designSystemStyles.gapMd}>
                    <QontyEmptyState
                        text={viewModel.loadErrorMessage.localized()}
                        darkMode={false}
                    />
                </VerticalContainer>
            ) : viewModel.allRows.length === 0 ? (
                <VerticalContainer width="100%" height="100%" alignment="center" justifyContent="center">
                    <QontyEmptyState
                        text={'No hay colaboradores para mostrar.'.localized()}
                        darkMode={false}
                    />
                </VerticalContainer>
            ) : isFirstUse && !showListInFirstUse ? (
                firstUseView
            ) : (
                tableView
            )}
            </div>

            <SendInvitationsModalScreen
                isOpen={viewModel.isInviteModalOpen}
                rows={viewModel.pendingInviteRows}
                isSending={viewModel.isSendingInvitations}
                onCancel={viewModel.cancelInvite}
                onConfirm={() => void handleConfirmInvitations()}
            />
            <Modal
                isOpen={viewModel.isDeviceDetailsOpen}
                onClose={viewModel.closeDeviceDetails}
                title={'Dispositivo'.localized()}
                subtitle={viewModel.deviceDetailsRow
                    ? [viewModel.deviceDetailsRow.name, viewModel.deviceDetailsRow.empresa]
                        .filter(Boolean)
                        .join(' · ')
                    : undefined}
                width="520px"
                height="auto"
                maxWidth="92vw"
            >
                {viewModel.deviceDetailsRow ? (
                    <VerticalContainer gap={designSystemStyles.gapLg} width="100%" alignment="stretch">
                        <HorizontalContainer width="100%" justifyContent="space-between" alignment="center" gap={designSystemStyles.gapSm}>
                            <HorizontalContainer width="auto" alignment="center" gap={designSystemStyles.gapSm}>
                                <HorizontalContainer
                                    width="auto"
                                    alignment="center"
                                    justifyContent="center"
                                    backgroundColor={designSystemStyles.colorBackgroundGray2}
                                    borderRadius={designSystemStyles.radiusItem}
                                    padding={designSystemStyles.paddingSm}
                                >
                                    <SystemIcon
                                        type={viewModel.deviceDetailsRow.platform === 'ios' ? 'apple' : 'android'}
                                        color={designSystemStyles.colorTextSecondary}
                                    />
                                </HorizontalContainer>
                                <VerticalContainer gap={designSystemStyles.gapXxxSm} alignment="left" width="auto">
                                    <Text
                                        markdown={true}
                                        text={`**${(viewModel.deviceDetailsRow.deviceModelLabel || viewModel.deviceDetailsRow.platformLabel || 'Dispositivo'.localized()).trim()}**`}
                                    />
                                    {viewModel.deviceDetailsRow.osLabel ? (
                                        <SmallText
                                            text={viewModel.deviceDetailsRow.osLabel}
                                            color={designSystemStyles.colorTextMuted}
                                        />
                                    ) : null}
                                </VerticalContainer>
                            </HorizontalContainer>
                            <StatusPill
                                label={viewModel.deviceDetailsRow.statusLabel}
                                dotColor={adoptionStatusDotColors[viewModel.deviceDetailsRow.status]}
                            />
                        </HorizontalContainer>
                        <HorizontalContainer
                            width="100%"
                            gridTemplateColumns="1fr 1fr"
                            gap={designSystemStyles.gapMd}
                        >
                            <DetailField
                                label={'Plataforma'.localized()}
                                value={viewModel.deviceDetailsRow.platformLabel}
                            />
                            <DetailField
                                label={'Versión de la app'.localized()}
                                value={viewModel.deviceDetailsRow.appVersionLabel}
                            />
                            <DetailField
                                label={'Instalada'.localized()}
                                value={viewModel.deviceDetailsRow.installedLabel}
                            />
                            <DetailField
                                label={'Última actividad'.localized()}
                                value={viewModel.deviceDetailsRow.lastActivityDetailLabel}
                            />
                        </HorizontalContainer>
                        <HorizontalContainer width="100%" justifyContent="flex-end">
                            <HollowedButton
                                text={'Cerrar'.localized()}
                                onClick={viewModel.closeDeviceDetails}
                            />
                        </HorizontalContainer>
                    </VerticalContainer>
                ) : <></>}
            </Modal>
        </VerticalContainer>
    );

    return (
        <SingleTabScreen
            title={'App Qontact'.localized()}
            subtitle={'Visualizá qué colaboradores instalaron la app Qontact y enviá invitaciones a los que faltan.'.localized()}
            flap={{
                id: 'adopcion-app',
                label: 'App Qontact'.localized(),
                content,
            }}
        />
    );
};

export default AppAdoptionScreen;
